import type { CustomerOrganisation } from '@/types/generated'

/**
 * Phone AI self-service minute usage helpers (progress colours and warning tiers).
 *
 * Progress bar **bands** (used / included): below 80%, from 80% to below 100%, and at cap
 * (used >= included, including overage). Fill colours are **plan-specific** (Figma): beginner
 * uses primary purple below 80% and semantic red from 80% onward; pro-style plans use primary
 * purple for all bands.
 *
 * Warning banner: hidden below 80%; informational from 80% to below 90%; urgent from 90%
 * to below 100%; limit reached when used meets or exceeds included (including overage).
 */

export type PhoneAiWarningTier = 'none' | 'info' | 'urgent' | 'limit'

type PhoneAiProgressBand = 'green' | 'yellow' | 'red'

/**
 * Normalise minute counts from the API. Missing fields, null, NaN, and negative values become 0.
 * (Math.max(0, undefined) is NaN in JS, which broke the progress label and tier logic.)
 */
export function coercePhoneAiMinuteCount(value: unknown): number {
  if (value === null || value === undefined || value === '') {
    return 0
  }
  const n = typeof value === 'string' ? Number.parseInt(value, 10) : Number(value)
  if (!Number.isFinite(n) || n < 0) {
    return 0
  }
  return Math.floor(n)
}

/** Mirrors backend ProductPlan.get_included_minutes for non-enterprise tiers (customer API shape). */
const PHONE_PLAN_INCLUDED_MINUTES: Readonly<Record<string, number>> = {
  phone_beginner_plan: 100,
  phone_professional_plan: 250,
  phone_professional_plus_plan: 700
}

/** Org fields needed to resolve included minutes (`GET /organisations/current/`). */
export type PhoneAiIncludedMinutesOrg = Pick<
  CustomerOrganisation,
  'phonebot_self_service_included_minutes' | 'phonebot_self_service_plan'
>

/**
 * Resolve included minutes: prefer API field, else derive from `phonebot_self_service_plan` when
 * the numeric field is missing, zero, or non-finite (stale clients / odd payloads). Enterprise
 * without API minutes cannot be inferred client-side.
 *
 * :param org: Organisation from `GET /organisations/current/`
 *
 * :return: Included minutes for UI thresholds, or 0
 */
export function resolvePhoneAiIncludedMinutes(
  org: PhoneAiIncludedMinutesOrg | null | undefined
): number {
  if (!org) {
    return 0
  }
  const minutes = Number(org.phonebot_self_service_included_minutes)
  if (Number.isFinite(minutes) && minutes > 0) {
    return minutes
  }
  return PHONE_PLAN_INCLUDED_MINUTES[String(org.phonebot_self_service_plan ?? '')] ?? 0
}

/** Whether usage UI should show (positive `resolvePhoneAiIncludedMinutes` result). */
export function shouldDisplayPhoneAiUsage(
  org: PhoneAiIncludedMinutesOrg | null | undefined
): boolean {
  return resolvePhoneAiIncludedMinutes(org) > 0
}

/** Width percentage for the progress fill, clamped to 0–100. */
export function getPhoneAiUsageBarPercent(usedMinutes: unknown, includedMinutes: unknown): number {
  const included = coercePhoneAiMinuteCount(includedMinutes)
  if (included <= 0) {
    return 0
  }
  const used = coercePhoneAiMinuteCount(usedMinutes)
  return Math.min(100, (used / included) * 100)
}

/**
 * Progress bar band from used / included ratio (included must be > 0).
 *
 * :param usedMinutes: Used minutes this period
 * :param includedMinutes: Included minutes in the plan bucket
 *
 * :return: `green` below 80%, `yellow` from 80% until below cap, `red` at or above cap
 */
function getPhoneAiProgressBand(
  usedMinutes: unknown,
  includedMinutes: unknown
): PhoneAiProgressBand {
  const included = coercePhoneAiMinuteCount(includedMinutes)
  if (included <= 0) {
    return 'green'
  }
  const used = coercePhoneAiMinuteCount(usedMinutes)
  if (used >= included) {
    return 'red'
  }
  const pct = (used / included) * 100
  if (pct < 80) {
    return 'green'
  }
  return 'yellow'
}

/**
 * Tailwind background class for the progress fill (Figma tokens: primary 600 `#9333ea`, danger 500 `#ef4444`).
 *
 * :param plan: `phonebot_self_service_plan` (beginner switches to red from 80% onward)
 * :param usedMinutes: Used minutes
 * :param includedMinutes: Included minutes
 *
 * :return: Tailwind `bg-*` class for the fill
 */
export function getPhoneAiProgressBarFillClass(
  plan: string | null | undefined,
  usedMinutes: unknown,
  includedMinutes: unknown
): string {
  const tier = getPhoneAiProgressBand(usedMinutes, includedMinutes)
  if (isPhoneAiBeginnerPlan(plan)) {
    return tier === 'green' ? 'bg-purple-600' : 'bg-red-500'
  }
  return 'bg-purple-600'
}

/**
 * Warning banner tier from usage; limit when used >= included (overage clamps bar at 100%).
 */
export function getPhoneAiWarningTier(
  usedMinutes: unknown,
  includedMinutes: unknown
): PhoneAiWarningTier {
  const included = coercePhoneAiMinuteCount(includedMinutes)
  if (included <= 0) {
    return 'none'
  }
  const used = coercePhoneAiMinuteCount(usedMinutes)
  if (used >= included) {
    return 'limit'
  }
  const ratio = used / included
  if (ratio < 0.8) {
    return 'none'
  }
  if (ratio < 0.9) {
    return 'info'
  }
  return 'urgent'
}

/** Remaining included minutes before overage (floored, never negative). */
export function getPhoneAiRemainingMinutes(usedMinutes: unknown, includedMinutes: unknown): number {
  const included = coercePhoneAiMinuteCount(includedMinutes)
  const used = coercePhoneAiMinuteCount(usedMinutes)
  return Math.max(0, included - used)
}

/**
 * Parse API billing period dates (`YYYY-MM-DD`, `DD.MM.YYYY`, or ISO) as local calendar dates.
 *
 * :param raw: Raw string from organisation API
 *
 * :return: Parsed date at local midnight, or null
 */
export function parsePhoneAiPeriodDate(raw: string | null | undefined): Date | null {
  if (raw == null || raw === '') {
    return null
  }
  const s = String(raw).trim()
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    return Number.isNaN(d.getTime()) ? null : d
  }
  const dmy = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s)
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]))
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Add whole calendar days in local time (billing boundaries).
 *
 * :param d: Base date
 * :param days: Number of days to add (can be negative)
 *
 * :return: New date
 */
export function addPhoneAiCalendarDays(d: Date, days: number): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  x.setDate(x.getDate() + days)
  return x
}

/**
 * Format as day + long month for banner copy (e.g. German "31. März").
 *
 * :param d: Date to format
 * :param locale: App locale code (`de`, `en`, `fr`)
 *
 * :return: Localised string without year
 */
export function formatPhoneAiBannerDayMonth(d: Date, locale: string): string {
  const loc = locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-GB'
  return new Intl.DateTimeFormat(loc, { day: 'numeric', month: 'long' }).format(d)
}

/** Whether the plan uses beginner (capped / deactivation) banner copy and bar styling. */
export function isPhoneAiBeginnerPlan(plan: string | null | undefined): boolean {
  return String(plan ?? '') === 'phone_beginner_plan'
}
